import { useEffect, useRef, useState, useCallback } from "react";
import { BrainCircuit, Send, Plus, RefreshCw, FileText, ChevronDown, ChevronUp, Check, X, Radar, AlertTriangle, TrendingUp, Info, Eye, Lightbulb, ClipboardList, Stethoscope, Copy, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ title, children, testid }) => (
  <div data-testid={testid} className="border border-border bg-card">
    <div className="border-b border-border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{title}</div>
    {children}
  </div>
);

const ENV_STYLE = {
  production: "border-[#00E676]/40 text-[#00E676]",
  simulator: "border-amber-500/40 text-amber-300",
  test: "border-neutral-600 text-neutral-400",
  preview: "border-primary/40 text-primary",
  internal: "border-sky-500/40 text-sky-300",
  nexus: "border-violet-500/40 text-violet-300",
};

const VERIF_LABEL = {
  observe_nexus: "observé par NEXUS",
  declare_projet: "déclaré par le projet",
  interprete_ia: "interprété par une IA",
};

const EnvChip = ({ env }) => (
  <span className={`border px-1.5 py-px font-mono text-[9px] uppercase tracking-[0.1em] ${ENV_STYLE[env] || ENV_STYLE.test}`}>{env || "?"}</span>
);

const Sources = ({ sources }) => {
  const [open, setOpen] = useState(false);
  if (!sources?.length) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)} data-testid="toggle-sources"
        className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 transition-colors duration-150 hover:text-primary">
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />} Sources réelles utilisées ({sources.length})
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1 border-l border-border pl-3">
          {sources.map((s, i) => (
            <li key={s.id || `src-${i}`} className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-neutral-500">
              {(s.environment || "").split("/").map((e) => <EnvChip key={e} env={e} />)}
              <span className="text-neutral-400">{s.label}</span>
              <span className="text-neutral-600">· {s.kind}{s.component ? ` · ${s.component}` : ""}{s.verification ? ` · ${VERIF_LABEL[s.verification] || s.verification}` : ""}{s.timestamp ? ` · ${s.timestamp.slice(0, 16)}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const diagToText = (d) => {
  const v = d.verdict || {};
  const enq = d.enquete || {};
  return [
    `ENQUÊTE DU MOTEUR DE DIAGNOSTIC — ${d.created_at}`,
    `Déclencheur : ${d.trigger}${d.project_id ? ` · Projet : ${d.project_id}` : ""}`,
    `Symptôme : ${d.symptom}`, "",
    "— HYPOTHÈSES —",
    ...(enq.hypotheses || []).map((h) => `${h.id || "•"} (plausibilité ${h.plausibilite_initiale ?? "?"}) : ${h.hypothese}`), "",
    "— VÉRIFICATIONS (lecture seule) —",
    ...(enq.verifications || []).map((x) => `• ${x.check}${x.project_id ? ` [${x.project_id}]` : ""} → ${typeof x.resultat === "string" ? x.resultat : JSON.stringify(x.resultat, null, 1)}`), "",
    "— VERDICT —",
    `Cause racine proposée : ${v.cause_racine}`,
    `Confiance : ${v.confiance ?? "?"}${v.hypothese_retenue ? ` · Hypothèse retenue : ${v.hypothese_retenue}` : ""}`,
    ...(v.preuves?.length ? ["Preuves :", ...v.preuves.map((p) => `  - ${p}`)] : []),
    ...(v.plan_reparation_propose?.length ? ["", "Plan de réparation PROPOSÉ (aucune action exécutée) :", ...v.plan_reparation_propose.map((s, i) => `  ${i + 1}. ${s}`)] : []),
    ...(v.limites ? ["", `Limites : ${v.limites}`] : []),
    "", d.provenance || "",
  ].join("\n");
};

const ReportModal = ({ report, onClose }) => {
  if (!report) return null;
  return (
    <div data-testid="report-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-md md:p-8" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col border border-primary/30 bg-[#0f0f0f] shadow-[0_0_40px_rgba(0,229,255,0.12)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <span className="truncate font-mono text-[11px] uppercase tracking-[0.25em] text-primary">{report.title}</span>
          <div className="flex shrink-0 gap-2">
            <button data-testid="report-copy-btn"
              onClick={() => navigator.clipboard.writeText(report.text)
                .then(() => toast.success("Rapport copié dans le presse-papiers"))
                .catch(() => toast.error("Copie refusée par le navigateur — sélectionne et copie manuellement"))}
              className="flex items-center gap-1.5 border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-300 transition-colors hover:border-primary/50 hover:text-primary">
              <Copy size={11} /> Copier
            </button>
            <button data-testid="report-close-btn" onClick={onClose}
              className="border border-border p-1.5 text-neutral-400 transition-colors hover:border-red-500/40 hover:text-red-300"><X size={12} /></button>
          </div>
        </div>
        <div data-testid="report-content" className="overflow-y-auto whitespace-pre-wrap px-6 py-5 font-mono text-base leading-relaxed text-neutral-200">{report.text}</div>
      </div>
    </div>
  );
};

export default function Brain() {
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [memory, setMemory] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [directives, setDirectives] = useState([]);
  const [signals, setSignals] = useState([]);
  const [advices, setAdvices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [autopilot, setAutopilot] = useState(null);
  const [newDirective, setNewDirective] = useState("");
  const [busy, setBusy] = useState({});
  const [showBriefing, setShowBriefing] = useState(false);
  const [collector, setCollector] = useState(null);
  const [collectorRunning, setCollectorRunning] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);
  const [diagSymptom, setDiagSymptom] = useState("");
  const [diagHistory, setDiagHistory] = useState([]);
  const [showDiagHistory, setShowDiagHistory] = useState(false);
  const [tab, setTab] = useState("dialogue");
  const [report, setReport] = useState(null);
  const [vitals, setVitals] = useState({});
  const endRef = useRef(null);

  const loadAll = useCallback(() => {
    api.get("/brain/overview").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/brain/sessions").then(({ data }) => setSessions(data)).catch(() => {});
    api.get("/brain/memory?limit=6").then(({ data }) => setMemory(data)).catch(() => {});
    api.get("/brain/knowledge").then(({ data }) => setKnowledge(data)).catch(() => {});
    api.get("/brain/directives").then(({ data }) => setDirectives(data)).catch(() => {});
    api.get("/brain/signals?limit=20").then(({ data }) => setSignals(data)).catch(() => {});
    api.get("/brain/advice?limit=20").then(({ data }) => setAdvices(data)).catch(() => {});
    api.get("/brain/orders?limit=30").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/brain/autopilot").then(({ data }) => setAutopilot(data)).catch(() => {});
    api.get("/collector/status").then(({ data }) => setCollector(data)).catch(() => {});
    api.get("/diagnostic/status").then(({ data }) => setDiag(data)).catch(() => {});
    api.get("/diagnostic/dossiers?limit=15").then(({ data }) => setDiagHistory(data)).catch(() => {});
    api.get("/brain/context-size").then(({ data }) => setVitals((v) => ({ ...v, ctx: data }))).catch(() => {});
    api.get("/projects").then(({ data }) => setVitals((v) => ({ ...v, projects: data }))).catch(() => {});
  }, []);

  const loadCollector = useCallback(() => {
    api.get("/collector/status").then(({ data }) => setCollector(data)).catch(() => {});
  }, []);

  const loadDiag = useCallback(() => {
    api.get("/diagnostic/status").then(({ data }) => setDiag(data)).catch(() => {});
    api.get("/diagnostic/dossiers?limit=15").then(({ data }) => setDiagHistory(data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streamText]);

  const openSession = async (sid) => {
    setSessionId(sid);
    if (!sid) { setMessages([]); return; }
    const { data } = await api.get(`/brain/sessions/${sid}/messages`);
    setMessages(data);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setStreaming(true);
    setStreamText("");
    try {
      const res = await fetch(`${API_BASE}/brain/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", full = "", finalSources = [], sid = sessionId, errText = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) { full += ev.delta; setStreamText(full); }
          if (ev.error) { errText = ev.error; }
          if (ev.done) { finalSources = ev.sources || []; sid = ev.session_id; }
        }
      }
      if (full) {
        setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: full, sources: finalSources }]);
      } else {
        setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", error: true, content: errText || "NEXUS n'a pas répondu cette fois-ci — réessaie, ta question est conservée ci-dessus." }]);
      }
      if (!sessionId && sid) { setSessionId(sid); api.get("/brain/sessions").then(({ data }) => setSessions(data)).catch(() => {}); }
    } catch {
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", error: true, content: "La connexion avec le Cerveau a été interrompue — réessaie dans un instant." }]);
    } finally {
      setStreaming(false);
      setStreamText("");
    }
  };

  const runDigest = async () => {
    setBusy((b) => ({ ...b, digest: true }));
    try {
      const { data } = await api.post("/brain/digest");
      data.created ? toast.success(`Digestion terminée — ${data.items_digested} éléments condensés`) : toast.info(data.reason);
      loadAll();
    } catch { toast.error("Digestion impossible"); } finally { setBusy((b) => ({ ...b, digest: false })); }
  };

  const runBriefing = async () => {
    setBusy((b) => ({ ...b, briefing: true }));
    try {
      await api.post("/brain/briefing");
      toast.success("Briefing généré");
      setShowBriefing(true);
      loadAll();
    } catch { toast.error("Briefing impossible"); } finally { setBusy((b) => ({ ...b, briefing: false })); }
  };

  const runWatch = async () => {
    setBusy((b) => ({ ...b, watch: true }));
    try {
      const { data } = await api.post("/brain/watch");
      if (data.created > 0) toast.success(`Veille terminée — ${data.created} signal(aux) émis`);
      else toast.info(data.reason || "Veille terminée — rien de significatif");
      loadAll();
    } catch { toast.error("Veille impossible"); } finally { setBusy((b) => ({ ...b, watch: false })); }
  };

  const runAdvise = async () => {
    setBusy((b) => ({ ...b, advise: true }));
    try {
      const { data } = await api.post("/brain/advise");
      if (data.created > 0) toast.success(`${data.created} conseil(s) émis pour les projets en production`);
      else toast.info(data.reason || "Aucun conseil utile à émettre — NEXUS préfère se taire");
      loadAll();
    } catch { toast.error("Génération de conseils impossible"); } finally { setBusy((b) => ({ ...b, advise: false })); }
  };

  const orderAction = async (oid, action) => {
    try {
      const { data } = await api.post(`/brain/orders/${oid}/${action}`);
      if (action === "verify") {
        const v = data.verdict === "resolu" ? "Preuve visible — ordre clos ✓" : data.verdict === "non_resolu" ? "Preuve absente — ESCALADE vers toi" : "Indéterminé — données insuffisantes pour trancher";
        (data.verdict === "resolu" ? toast.success : data.verdict === "non_resolu" ? toast.error : toast.info)(v);
      } else {
        toast.success(action === "approve" ? "Ordre signé et envoyé au projet" : action === "reject" ? "Ordre rejeté" : action === "promote" ? "Ordre promu — signature requise" : "Ordre clos");
      }
      api.get("/brain/orders?limit=30").then(({ data }) => setOrders(data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    }
  };

  const signalAction = async (sid, action) => {
    await api.patch(`/brain/signals/${sid}`, { action });
    api.get("/brain/signals?limit=20").then(({ data }) => setSignals(data));
  };

  const addDirective = async () => {
    if (!newDirective.trim()) return;
    await api.post("/brain/directives", { text: newDirective.trim() });
    setNewDirective("");
    api.get("/brain/directives").then(({ data }) => setDirectives(data));
    toast.success("Directive mémorisée");
  };

  const knowledgeAction = async (kid, action) => {
    await api.patch(`/brain/knowledge/${kid}`, { action });
    api.get("/brain/knowledge").then(({ data }) => setKnowledge(data));
    toast.success(action === "confirm" ? "Connaissance confirmée" : "Connaissance invalidée");
  };

  const openDiagReport = async (id) => {
    try {
      const { data } = await api.get(`/diagnostic/dossiers/${id}`);
      setReport({ title: `Enquête du Moteur de Diagnostic — ${fmtDate(data.created_at)}`, text: diagToText(data) });
    } catch { toast.error("Enquête introuvable"); }
  };

  const briefing = overview?.latest_briefing;
  const proposedCount = orders.filter((o) => o.status === "proposed").length;
  const newSignals = signals.filter((s) => s.status === "new").length;
  const prods = (vitals.projects || []).filter((p) => p.environment === "production");
  const prodOnline = prods.filter((p) => p.last_status === "online").length;
  const ctx = vitals.ctx;
  const inputCls = "w-full border border-border bg-[#050505] px-3 py-2.5 font-mono text-lg text-white outline-none transition-colors duration-150 focus:border-primary";

  return (
    <div data-testid="brain-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.06] blur-3xl" />
      <div className="relative flex items-end justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-4xl font-bold tracking-tight text-white">
            <BrainCircuit className="text-primary drop-shadow-[0_0_10px_rgba(0,229,255,0.5)]" size={34} /> Cerveau
          </h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            NEXUS — observe · comprend · mémorise · conseille — n'agit jamais seul
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="run-advise-button" onClick={runAdvise} disabled={busy.advise}
            className="flex items-center gap-2 border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-150 hover:border-amber-400/50 hover:text-amber-300 disabled:opacity-40">
            <Lightbulb size={12} className={busy.advise ? "animate-pulse" : ""} /> {busy.advise ? "Réflexion…" : "Conseiller maintenant"}
          </button>
          <button data-testid="run-watch-button" onClick={runWatch} disabled={busy.watch}
            className="flex items-center gap-2 border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-150 hover:border-violet-400/50 hover:text-violet-300 disabled:opacity-40">
            <Radar size={12} className={busy.watch ? "animate-spin" : ""} /> {busy.watch ? "Veille…" : "Veiller maintenant"}
          </button>
          <button data-testid="run-digest-button" onClick={runDigest} disabled={busy.digest}
            className="flex items-center gap-2 border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-150 hover:border-primary/50 hover:text-primary disabled:opacity-40">
            <RefreshCw size={12} className={busy.digest ? "animate-spin" : ""} /> {busy.digest ? "Digestion…" : "Digérer maintenant"}
          </button>
          <button data-testid="run-briefing-button" onClick={runBriefing} disabled={busy.briefing}
            className="flex items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black transition-opacity duration-150 hover:opacity-90 disabled:opacity-40">
            <FileText size={12} /> {busy.briefing ? "Réflexion…" : "Générer le briefing"}
          </button>
        </div>
      </div>

      <div data-testid="brain-vitals" className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div data-testid="vital-reseau" className="border border-white/10 bg-[#121212]/90 px-4 py-3 backdrop-blur-sm">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Réseau production</div>
          <div className={`mt-1 font-mono text-2xl font-bold ${prods.length && prodOnline === prods.length ? "text-[#00E676]" : "text-amber-300"}`}>
            {prodOnline}<span className="text-sm text-neutral-500">/{prods.length} en ligne</span>
          </div>
        </div>
        <button data-testid="vital-ordres" onClick={() => setTab("ordres")} className="border border-white/10 bg-[#121212]/90 px-4 py-3 text-left backdrop-blur-sm transition-all duration-200 hover:border-red-500/40 hover:shadow-[0_0_16px_rgba(239,68,68,0.14)]">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">À signer</div>
          <div className={`mt-1 font-mono text-2xl font-bold ${proposedCount ? "text-amber-300" : "text-neutral-300"}`}>{proposedCount}<span className="text-sm text-neutral-500"> ordre(s)</span></div>
        </button>
        <button data-testid="vital-signaux" onClick={() => setTab("ordres")} className="border border-white/10 bg-[#121212]/90 px-4 py-3 text-left backdrop-blur-sm transition-all duration-200 hover:border-violet-500/40 hover:shadow-[0_0_16px_rgba(168,85,247,0.14)]">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Signaux nouveaux</div>
          <div className={`mt-1 font-mono text-2xl font-bold ${newSignals ? "text-violet-300" : "text-neutral-300"}`}>{newSignals}</div>
        </button>
        <div data-testid="vital-contexte" className="border border-white/10 bg-[#121212]/90 px-4 py-3 backdrop-blur-sm">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Contexte du Cerveau</div>
          {ctx ? (
            <>
              <div className={`mt-1 font-mono text-2xl font-bold ${ctx.estimated_tokens > ctx.alert_threshold_tokens ? "text-red-400" : ctx.estimated_tokens > ctx.alert_threshold_tokens * 0.85 ? "text-amber-300" : "text-neutral-200"}`}>
                {(ctx.estimated_tokens / 1000).toFixed(1)}k<span className="text-sm text-neutral-500">/{(ctx.alert_threshold_tokens / 1000).toFixed(0)}k tokens</span>
              </div>
              <div className="mt-1.5 h-1 w-full bg-neutral-800">
                <div className={`h-1 ${ctx.estimated_tokens > ctx.alert_threshold_tokens ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.7)]" : ctx.estimated_tokens > ctx.alert_threshold_tokens * 0.85 ? "bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.7)]" : "bg-primary shadow-[0_0_8px_rgba(0,229,255,0.7)]"}`}
                  style={{ width: `${Math.min(100, (ctx.estimated_tokens / ctx.alert_threshold_tokens) * 100)}%` }} />
              </div>
            </>
          ) : <div className="mt-1 font-mono text-2xl font-bold text-neutral-600">…</div>}
        </div>
        <button data-testid="vital-justesse" onClick={() => setTab("enquetes")} className="border border-white/10 bg-[#121212]/90 px-4 py-3 text-left backdrop-blur-sm transition-all duration-200 hover:border-amber-500/40 hover:shadow-[0_0_16px_rgba(245,158,11,0.14)]">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Justesse diagnostic</div>
          <div className="mt-1 font-mono text-2xl font-bold text-neutral-200">
            {diag?.justesse?.evalues ? `${Math.round((diag.justesse.taux_justesse ?? 0) * 100)}%` : "—"}
            <span className="text-sm text-neutral-500">{diag?.justesse?.evalues ? ` ${diag.justesse.justes}/${diag.justesse.evalues}` : " non évalué"}</span>
          </div>
        </button>
      </div>

      <div data-testid="brain-tabs" className="flex flex-wrap gap-1 border-b border-border">
        {[["dialogue", "Dialogue"], ["rapports", "Rapports"], ["enquetes", "Enquêtes"], ["ordres", "Ordres & Signaux"]].map(([id, label]) => (
          <button key={id} data-testid={`brain-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${tab === id ? "border-b-2 border-primary text-primary [text-shadow:0_0_12px_rgba(0,229,255,0.45)]" : "text-neutral-500 hover:text-neutral-300"}`}>
            {label}
            {id === "ordres" && proposedCount + newSignals > 0 && (
              <span className="ml-1.5 border border-red-500/40 bg-red-500/10 px-1.5 py-px text-[9px] text-red-300">{proposedCount + newSignals}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "rapports" && (<>
      <Section title={`Briefing global${briefing ? ` — ${fmtDate(briefing.created_at)}` : ""}`} testid="briefing-panel">
        {!briefing && <div className="px-5 py-6 font-mono text-lg text-neutral-600">Aucun briefing encore généré — clique « Générer le briefing »</div>}
        {briefing && (
          <div className="px-5 py-4">
            <div className={`whitespace-pre-wrap font-mono text-lg leading-relaxed text-neutral-200 ${showBriefing ? "" : "max-h-40 overflow-hidden"}`}>
              {briefing.content}
            </div>
            <div className="mt-2 flex items-center gap-4">
              <button data-testid="toggle-briefing" onClick={() => setShowBriefing(!showBriefing)}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary transition-opacity duration-150 hover:opacity-80">
                {showBriefing ? "Réduire" : "Lire tout"}
              </button>
              <button data-testid="briefing-fullscreen"
                onClick={() => setReport({ title: `Briefing global — ${fmtDate(briefing.created_at)}`, text: briefing.content })}
                className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-primary">
                <Maximize2 size={11} /> Plein écran · Copier
              </button>
            </div>
            <Sources sources={briefing.sources} />
          </div>
        )}
      </Section>
      </>)}

      {tab === "ordres" && (<>
      <div data-testid="signals-panel" className="border border-violet-500/25 bg-card shadow-[0_0_20px_rgba(168,85,247,0.06)]">
        <div className="flex items-center justify-between border-b border-violet-500/25 px-4 py-2.5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-violet-300">
            <Radar size={13} /> Signaux proactifs — veille toutes les 30 min
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">
            {signals.filter((s) => s.status === "new").length} nouveau(x)
          </span>
        </div>
        <div data-testid="signals-list" className="max-h-72 divide-y divide-border overflow-y-auto">
          {signals.length === 0 && (
            <div className="px-5 py-6 font-mono text-lg text-neutral-600">
              Aucun signal — la veille n'a rien détecté de significatif. Le silence est bon signe.
            </div>
          )}
          {signals.map((s) => {
            const KindIcon = s.kind === "probleme" ? AlertTriangle : s.kind === "opportunite" ? TrendingUp : Info;
            const kindCls = s.kind === "probleme" ? "border-red-500/40 bg-red-500/10 text-red-400" : s.kind === "opportunite" ? "border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]" : "border-neutral-600 bg-neutral-800/40 text-neutral-300";
            return (
              <div key={s.id} data-testid={`signal-${s.id}`} className={`px-5 py-4 ${s.status === "new" ? "" : "opacity-50"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] ${kindCls}`}>
                        <KindIcon size={11} /> {s.kind === "probleme" ? "Problème" : s.kind === "opportunite" ? "Opportunité" : "Info"}
                      </span>
                      {s.severity === "critique" && <span className="border border-red-500/60 bg-red-500/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-red-300">critique</span>}
                      {s.project && <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{s.project}</span>}
                      <span className="font-mono text-lg font-bold text-white">{s.title}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap font-mono text-lg leading-relaxed text-neutral-300">{s.analysis}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <span className="font-mono text-[10px] text-neutral-600">{fmtDate(s.created_at)}</span>
                    {s.status === "new" && (
                      <div className="flex gap-1">
                        <button data-testid={`signal-seen-${s.id}`} onClick={() => signalAction(s.id, "seen")} title="Vu"
                          className="flex items-center gap-1 border border-border px-2 py-1 font-mono text-[10px] uppercase text-neutral-300 transition-colors duration-150 hover:border-[#00E676]/50 hover:text-[#00E676]">
                          <Eye size={11} /> Vu
                        </button>
                        <button data-testid={`signal-dismiss-${s.id}`} onClick={() => signalAction(s.id, "dismiss")} title="Ignorer"
                          className="border border-border p-1.5 text-neutral-500 transition-colors duration-150 hover:border-red-500/50 hover:text-red-400">
                          <X size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </>)}

      {tab === "rapports" && (<>
      <div data-testid="collector-panel" className="border border-cyan-500/25 bg-card shadow-[0_0_20px_rgba(6,182,212,0.06)]">
        <div className="flex items-center justify-between border-b border-cyan-500/25 px-4 py-2.5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
            <Radar size={13} /> Collecteur Gemini — Shadow lecture seule · interprète_ia · jamais source de vérité
          </div>
          <div className="flex items-center gap-2">
            {collector?.settings && (
              <button data-testid="collector-toggle"
                onClick={async () => {
                  try {
                    await api.patch("/collector/settings", { enabled: !collector.settings.enabled });
                    loadCollector();
                    toast.success(collector.settings.enabled ? "Collecteur désactivé" : "Collecteur activé");
                  } catch { toast.error("Modification refusée"); }
                }}
                className={`flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                  collector.settings.enabled ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" : "border-border text-neutral-500 hover:text-neutral-300"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${collector.settings.enabled ? "bg-cyan-400 animate-pulse" : "bg-neutral-600"}`} />
                {collector.settings.enabled ? `ON · ${collector.settings.cycles_per_day} cycles/j` : "OFF"}
              </button>
            )}
            <button data-testid="collector-run-btn" disabled={collectorRunning}
              onClick={async () => {
                setCollectorRunning(true);
                try {
                  const { data } = await api.post("/collector/run");
                  toast.success(`Collecte terminée — ~${data.cout?.tokens_estimes} tokens`);
                  loadCollector();
                } catch (e) { toast.error(e?.response?.data?.detail || "Collecte refusée"); }
                finally { setCollectorRunning(false); }
              }}
              className="flex items-center gap-1.5 border border-cyan-500/50 bg-cyan-500/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-300 transition-colors hover:bg-cyan-500/20 disabled:opacity-50">
              <RefreshCw size={11} className={collectorRunning ? "animate-spin" : ""} /> {collectorRunning ? "Collecte…" : "Lancer une collecte"}
            </button>
          </div>
        </div>
        <div className="p-4 font-mono text-lg text-neutral-300">
          {collector?.dernier_dossier ? (
            <div data-testid="collector-latest">
              <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                Dernier dossier · {fmtDate(collector.dernier_dossier.created_at)} · {collector.dernier_dossier.trigger} ·
                ~{collector.dernier_dossier.cout?.tokens_estimes} tokens · {collector.dernier_dossier.contradictions} contradiction(s) · {collector.dernier_dossier.lacunes} lacune(s)
              </div>
              <p className="whitespace-pre-wrap leading-relaxed">{collector.dernier_dossier.etat_reseau}</p>
              <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-cyan-500/70">{collector.dernier_dossier.provenance}</div>
              <button data-testid="collector-full-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.get("/collector/dossiers/latest");
                    setReport({ title: `Dossier du Collecteur Gemini — ${fmtDate(data.created_at)}`,
                      text: `${data.provenance}\nDéclencheur : ${data.trigger} · ~${data.cout?.tokens_estimes} tokens · projets interrogés : ${(data.projets_interroges || []).join(", ") || "aucun"}\n\n${JSON.stringify(data.dossier, null, 2)}` });
                  } catch { toast.error("Dossier introuvable"); }
                }}
                className="mt-2 flex items-center gap-1.5 border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300">
                <Maximize2 size={11} /> Dossier complet · Copier
              </button>
            </div>
          ) : (
            <p data-testid="collector-empty" className="text-neutral-500">Aucun dossier — lance une première collecte.</p>
          )}
        </div>
      </div>
      </>)}

      {tab === "enquetes" && (<>
      <div data-testid="diagnostic-panel" className="border border-amber-500/25 bg-card shadow-[0_0_20px_rgba(245,158,11,0.06)]">
        <div className="flex items-center justify-between border-b border-amber-500/25 px-4 py-2.5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300">
            <Stethoscope size={13} /> Moteur de Diagnostic — Niveau 1 · lecture seule · aucune action, plan proposé à Mario
          </div>
          <div className="flex items-center gap-2">
            {diag?.settings && (
              <>
                <button data-testid="diagnostic-auto-toggle"
                  onClick={async () => {
                    try {
                      await api.patch("/diagnostic/settings", { auto_triggers: !diag.settings.auto_triggers });
                      loadDiag();
                      toast.success(diag.settings.auto_triggers ? "Déclencheurs auto coupés" : "Déclencheurs auto activés (alerte critique, échec de livraison)");
                    } catch { toast.error("Modification refusée"); }
                  }}
                  title="Déclencheurs automatiques : alerte critique reçue ou échec de livraison d'un ordre"
                  className={`flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                    diag.settings.auto_triggers ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-border text-neutral-500 hover:text-neutral-300"}`}>
                  AUTO {diag.settings.auto_triggers ? "ON" : "OFF"}
                </button>
                <button data-testid="diagnostic-toggle"
                  onClick={async () => {
                    try {
                      await api.patch("/diagnostic/settings", { enabled: !diag.settings.enabled });
                      loadDiag();
                      toast.success(diag.settings.enabled ? "Moteur de Diagnostic désactivé" : "Moteur de Diagnostic activé");
                    } catch { toast.error("Modification refusée"); }
                  }}
                  className={`flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                    diag.settings.enabled ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-border text-neutral-500 hover:text-neutral-300"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${diag.settings.enabled ? "bg-amber-400 animate-pulse" : "bg-neutral-600"}`} />
                  {diag.settings.enabled ? `ON · ${diag.settings.max_per_day}/j max` : "OFF"}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="space-y-3 p-4 font-mono text-lg text-neutral-300">
          <div className="flex gap-2">
            <input data-testid="diagnostic-symptom-input" value={diagSymptom}
              onChange={(e) => setDiagSymptom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !diagRunning && diagSymptom.trim().length >= 5) document.querySelector('[data-testid="diagnostic-run-btn"]')?.click(); }}
              placeholder="Décris un symptôme… (ex. : un ordre vers Verbe revient en 401 à la livraison)"
              className="flex-1 border border-border bg-background px-3 py-1.5 font-mono text-sm text-neutral-200 placeholder:text-neutral-600 focus:border-amber-500/50 focus:outline-none" />
            <button data-testid="diagnostic-run-btn" disabled={diagRunning || diagSymptom.trim().length < 5}
              onClick={async () => {
                setDiagRunning(true);
                try {
                  const { data } = await api.post("/diagnostic/run", { symptom: diagSymptom.trim() });
                  toast.success(`Diagnostic terminé — confiance ${data.confiance ?? "?"} · ${data.verifications} vérification(s)`);
                  setDiagSymptom("");
                  loadDiag();
                } catch (e) { toast.error(e?.response?.data?.detail || "Diagnostic refusé"); }
                finally { setDiagRunning(false); }
              }}
              className="flex shrink-0 items-center gap-1.5 border border-amber-500/50 bg-amber-500/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-amber-300 transition-colors hover:bg-amber-500/20 disabled:opacity-50">
              <RefreshCw size={11} className={diagRunning ? "animate-spin" : ""} /> {diagRunning ? "Enquête…" : "Lancer un diagnostic"}
            </button>
          </div>
          {diag?.dernier_dossier ? (
            <div data-testid="diagnostic-latest">
              <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                Dernière enquête · {fmtDate(diag.dernier_dossier.created_at)} · {diag.dernier_dossier.trigger} ·
                confiance {diag.dernier_dossier.confiance ?? "?"} · {diag.dernier_dossier.hypotheses} hypothèse(s) · {diag.dernier_dossier.verifications} vérification(s) ·
                ~{diag.dernier_dossier.cout?.tokens_estimes} tokens
              </div>
              <p className="text-[11px] uppercase tracking-[0.1em] text-neutral-500">Symptôme : <span className="normal-case tracking-normal text-neutral-400">{diag.dernier_dossier.symptom}</span></p>
              <p className="mt-1 whitespace-pre-wrap leading-relaxed"><span className="text-amber-300">Cause racine proposée :</span> {diag.dernier_dossier.cause_racine}</p>
              {Array.isArray(diag.dernier_dossier.plan_reparation_propose) && diag.dernier_dossier.plan_reparation_propose.length > 0 && (
                <div className="mt-2 border-l-2 border-amber-500/30 pl-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Plan de réparation PROPOSÉ (aucune action exécutée)</div>
                  {diag.dernier_dossier.plan_reparation_propose.map((step, i) => (
                    <p key={`plan-${i}-${String(step).slice(0, 24)}`} className="text-sm leading-relaxed text-neutral-400">{i + 1}. {step}</p>
                  ))}
                </div>
              )}
              <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-amber-500/70">{diag.dernier_dossier.provenance}</div>
              <button data-testid="diagnostic-full-btn" onClick={() => openDiagReport(diag.dernier_dossier.id)}
                className="mt-2 flex items-center gap-1.5 border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 transition-colors hover:border-amber-500/40 hover:text-amber-300">
                <Maximize2 size={11} /> Enquête complète · Copier
              </button>
            </div>
          ) : (
            <p data-testid="diagnostic-empty" className="text-neutral-500">Aucune enquête — décris un symptôme et lance un diagnostic.</p>
          )}
          {diagHistory.length > 0 && (
            <div data-testid="diagnostic-history">
              <button data-testid="diagnostic-history-toggle" onClick={() => setShowDiagHistory((v) => !v)}
                className="flex items-center gap-1.5 border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 transition-colors hover:border-amber-500/40 hover:text-amber-300">
                {showDiagHistory ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                Historique des enquêtes ({diag?.dossiers_total ?? diagHistory.length})
                {diag?.justesse?.evalues > 0 && (
                  <span className="ml-1 text-amber-400/80">
                    · justesse {Math.round((diag.justesse.taux_justesse ?? 0) * 100)}% ({diag.justesse.justes}/{diag.justesse.evalues} évaluées)
                  </span>
                )}
              </button>
              {showDiagHistory && (
                <div className="mt-2 max-h-80 divide-y divide-border overflow-y-auto border border-border">
                  {diagHistory.map((h) => (
                    <div key={h.id} data-testid={`diagnostic-history-item-${h.id}`} className="px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                            {fmtDate(h.created_at)} · {h.trigger} · confiance {h.confiance ?? "?"} · {h.verifications} vérif.
                            {h.project_id ? ` · ${h.project_id}` : ""}
                          </div>
                          <p className="truncate text-sm text-neutral-400" title={h.symptom}>{h.symptom}</p>
                          <p className="mt-0.5 text-sm leading-snug text-neutral-300" title={h.cause_racine}>
                            <span className="text-amber-300/80">Verdict :</span> {h.cause_racine}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button data-testid={`diagnostic-view-${h.id}`} onClick={() => openDiagReport(h.id)} title="Voir l'enquête complète"
                            className="border border-border p-1 text-neutral-500 transition-colors hover:border-amber-500/40 hover:text-amber-300"><Maximize2 size={11} /></button>
                          {h.review ? (
                            <span data-testid={`diagnostic-review-badge-${h.id}`}
                              className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] ${
                                h.review.evaluation === "juste" ? "border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]"
                                : h.review.evaluation === "faux" ? "border-red-500/40 bg-red-500/10 text-red-300"
                                : "border-border bg-neutral-800/40 text-neutral-400"}`}>
                              {h.review.evaluation}
                            </span>
                          ) : (
                            ["juste", "faux", "incertain"].map((ev) => (
                              <button key={ev} data-testid={`diagnostic-review-${ev}-${h.id}`}
                                onClick={async () => {
                                  try {
                                    let note = null;
                                    if (ev === "faux") {
                                      note = window.prompt("Pourquoi ce verdict est-il faux ? (optionnel — ta correction servira de leçon au moteur pour ses prochaines enquêtes)") || null;
                                    }
                                    await api.post(`/diagnostic/dossiers/${h.id}/review`, { evaluation: ev, note });
                                    toast.success(ev === "faux" ? "Verdict marqué faux — le moteur retiendra la leçon" : `Verdict évalué « ${ev} » — merci, la justesse du moteur est mise à jour`);
                                    loadDiag();
                                  } catch { toast.error("Évaluation refusée"); }
                                }}
                                title={`Marquer ce verdict comme ${ev}`}
                                className={`border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${
                                  ev === "juste" ? "border-[#00E676]/30 text-[#00E676]/70 hover:bg-[#00E676]/10 hover:text-[#00E676]"
                                  : ev === "faux" ? "border-red-500/30 text-red-400/70 hover:bg-red-500/10 hover:text-red-300"
                                  : "border-border text-neutral-500 hover:text-neutral-300"}`}>
                                {ev === "incertain" ? "?" : ev}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </>)}

      {tab === "ordres" && (<>
      <div data-testid="orders-panel" className="border border-red-500/25 bg-card shadow-[0_0_20px_rgba(239,68,68,0.06)]">
        <div className="flex items-center justify-between border-b border-red-500/25 px-4 py-2.5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-red-300">
            <ClipboardList size={13} /> Bureau des ordres — priorité basse : auto · autres : signature de Mario
          </div>
          <div className="flex items-center gap-3">
            {autopilot && (
              <button data-testid="autopilot-toggle"
                onClick={async () => {
                  try {
                    const { data } = await api.patch("/brain/autopilot", { enabled: !autopilot.enabled });
                    setAutopilot(data);
                    toast.success(data.enabled ? "Palier AUTO activé" : "Palier AUTO désactivé — tout exige ta signature");
                  } catch { toast.error("Modification refusée"); }
                }}
                title={autopilot.enabled ? `Ordres basse priorité auto-exécutés (${autopilot.used_today}/${autopilot.daily_cap} aujourd'hui). Cliquer pour tout repasser en signature.` : "Palier AUTO coupé : tout ordre exige ta signature. Cliquer pour réactiver."}
                className={`flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                  autopilot.enabled ? "border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]" : "border-border text-neutral-500 hover:text-neutral-300"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${autopilot.enabled ? "bg-[#00E676] animate-pulse" : "bg-neutral-600"}`} />
                AUTO {autopilot.enabled ? `${autopilot.used_today}/${autopilot.daily_cap}` : "OFF"}
              </button>
            )}
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">
              {orders.filter((o) => o.status === "proposed").length} en attente de signature
            </span>
          </div>
        </div>
        <div data-testid="orders-list" className="max-h-96 divide-y divide-border overflow-y-auto">
          {orders.length === 0 && (
            <div className="px-5 py-6 font-mono text-lg text-neutral-600">
              Aucun ordre — la veille n'a détecté aucune situation réelle justifiant une intervention
            </div>
          )}
          {orders.map((o) => {
            let ST = {
              proposed: ["PROPOSÉ — signature requise", "border-amber-500/50 bg-amber-500/10 text-amber-300"],
              surveillance: ["SURVEILLANCE — à vérifier (non confirmé)", "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"],
              dispatched: ["Envoyé — attente du projet", "border-sky-500/40 bg-sky-500/10 text-sky-300"],
              responded: ["Réponse reçue", "border-violet-500/40 bg-violet-500/10 text-violet-300"],
              closed: ["COMPLÉTÉ — clos sur preuve", "border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]"],
              escalated: ["ESCALADÉ", "border-red-500/60 bg-red-500/15 text-red-300"],
              rejected: ["Rejeté", "border-neutral-700 bg-neutral-800/40 text-neutral-500"],
            }[o.status] || [o.status, "border-neutral-700 text-neutral-400"];
            const d = o.delivery || {};
            if (o.status === "dispatched") {
              if (d.status === "failed") ST = [`SIGNÉ — NON LIVRÉ (${(d.error || "échec").slice(0, 40)})`, "border-red-500/60 bg-red-500/15 text-red-300"];
              else if (d.status === "retrying") ST = ["SIGNÉ — ENVOI EN COURS (réessais)", "border-amber-500/50 bg-amber-500/10 text-amber-300"];
              else if (d.status === "delivered") ST = ["ENVOYÉ — ACQUITTÉ par le projet", "border-sky-500/40 bg-sky-500/10 text-sky-300"];
              else ST = ["SIGNÉ — livraison non confirmée", "border-amber-500/50 bg-amber-500/10 text-amber-300"];
            }
            return (
              <div key={o.id} data-testid={`order-${o.id}`} className={`px-5 py-4 ${["closed", "rejected"].includes(o.status) ? "opacity-50" : ""}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] ${ST[1]}`}>{ST[0]}</span>
                  {o.auto_approved && (
                    <span data-testid={`order-auto-badge-${o.id}`} title="Exécuté automatiquement (palier AUTO, priorité basse) — sans signature"
                      className="border border-[#00E676]/40 bg-[#00E676]/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#00E676]">⚙ AUTO</span>
                  )}
                  <span className="border border-border px-2 py-0.5 font-mono text-[10px] uppercase text-neutral-300">{o.project}</span>
                  <span className={`font-mono text-[10px] uppercase ${["haute", "critique"].includes(o.priorite) ? "text-red-400" : "text-neutral-500"}`}>priorité {o.priorite}</span>
                  <span className="ml-auto font-mono text-[10px] text-neutral-600">{fmtDate(o.created_at)} · {o.origin}</span>
                </div>
                <div className="mt-2 space-y-1 font-mono text-base leading-relaxed text-neutral-300">
                  <p><span className="text-neutral-500">Constat :</span> {o.constat}</p>
                  {o.impact && <p><span className="text-neutral-500">Impact :</span> {o.impact}</p>}
                  <p className="text-white"><span className="text-neutral-500">Ordre :</span> {o.ordre}</p>
                  {o.preuve_attendue && <p><span className="text-neutral-500">Preuve attendue :</span> {o.preuve_attendue}</p>}
                  {o.delai_suivi && <p><span className="text-neutral-500">Suivi :</span> {o.delai_suivi}</p>}
                </div>
                {o.response && (
                  <div className="mt-2 border border-violet-500/20 bg-violet-500/5 px-3 py-2 font-mono text-sm leading-relaxed text-neutral-300">
                    <p className="font-bold text-violet-300">Réponse du projet : {o.response.decision}</p>
                    {o.response.cause && <p>Cause : {o.response.cause}</p>}
                    {o.response.action && <p>Action : {o.response.action}</p>}
                    {o.response.tests && <p>Tests : {o.response.tests}</p>}
                    {o.response.resultat && <p>Résultat : {o.response.resultat}</p>}
                  </div>
                )}
                {o.verdict && (
                  <p className={`mt-2 font-mono text-sm ${o.verdict.verdict === "resolu" ? "text-[#00E676]" : o.verdict.verdict === "non_resolu" ? "text-red-400" : "text-neutral-400"}`}>
                    Contrôle NEXUS : {o.verdict.verdict} — {o.verdict.justification}
                  </p>
                )}
                <div className="mt-3 flex gap-2">
                  {o.status === "surveillance" && (
                    <>
                      <button data-testid={`order-promote-${o.id}`} onClick={() => orderAction(o.id, "promote")}
                        title="Constat non confirmé par une source récente — promouvoir le transforme en ordre à signer"
                        className="border border-cyan-500/50 bg-cyan-500/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-300 transition-colors duration-150 hover:bg-cyan-500/20">
                        Promouvoir — demander signature
                      </button>
                      <button data-testid={`order-reject-${o.id}`} onClick={() => orderAction(o.id, "reject")}
                        className="border border-red-500/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 transition-colors duration-150 hover:bg-red-500/10">
                        Rejeter
                      </button>
                    </>
                  )}
                  {o.status === "proposed" && (
                    <>
                      <button data-testid={`order-approve-${o.id}`} onClick={() => orderAction(o.id, "approve")}
                        className="border border-[#00E676]/50 bg-[#00E676]/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#00E676] transition-colors duration-150 hover:bg-[#00E676]/20">
                        Approuver et envoyer
                      </button>
                      <button data-testid={`order-reject-${o.id}`} onClick={() => orderAction(o.id, "reject")}
                        className="border border-red-500/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 transition-colors duration-150 hover:bg-red-500/10">
                        Rejeter
                      </button>
                    </>
                  )}
                  {o.status === "dispatched" && (
                    <button data-testid={`order-redispatch-${o.id}`} onClick={() => orderAction(o.id, "redispatch")}
                      title="Remet une livraison en file vers le projet (utile si la précédente a échoué)"
                      className="border border-amber-400/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-300 transition-colors duration-150 hover:bg-amber-400/10">
                      Renvoyer
                    </button>
                  )}
                  {o.status === "responded" && (
                    <>
                      <button data-testid={`order-verify-${o.id}`} onClick={() => orderAction(o.id, "verify")}
                        className="border border-primary/50 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-primary transition-colors duration-150 hover:bg-primary/10">
                        Vérifier la preuve
                      </button>
                      <button data-testid={`order-close-${o.id}`} onClick={() => orderAction(o.id, "close")}
                        className="border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 transition-colors duration-150 hover:text-white">
                        Clore manuellement
                      </button>
                    </>
                  )}
                  {o.status === "escalated" && (
                    <button data-testid={`order-close-${o.id}`} onClick={() => orderAction(o.id, "close")}
                      className="border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 transition-colors duration-150 hover:text-white">
                      Clore (traité hors NEXUS)
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </>)}

      {tab === "rapports" && (<>
      <div data-testid="advice-panel" className="border border-amber-500/25 bg-card shadow-[0_0_20px_rgba(245,158,11,0.06)]">
        <div className="flex items-center justify-between border-b border-amber-500/25 px-4 py-2.5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300">
            <Lightbulb size={13} /> Conseils aux projets — non contraignants
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">
            générés avec le briefing quotidien · projets en production
          </span>
        </div>
        <div data-testid="advice-list" className="max-h-72 divide-y divide-border overflow-y-auto">
          {advices.length === 0 && (
            <div className="px-5 py-6 font-mono text-lg text-neutral-600">
              Aucun conseil émis — clique « Conseiller maintenant » ou attends le prochain briefing
            </div>
          )}
          {advices.map((a) => (
            <div key={a.id} data-testid={`advice-${a.id}`} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-amber-300">{a.project}</span>
                    <span className="font-mono text-lg font-bold text-white">{a.title}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap font-mono text-lg leading-relaxed text-neutral-300">{a.advice}</p>
                  {a.rationale && <p className="mt-1.5 font-mono text-sm leading-relaxed text-neutral-500">Fondé sur : {a.rationale}</p>}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <span className="font-mono text-[10px] text-neutral-600">{fmtDate(a.created_at)}</span>
                  <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] ${a.retrieved_at ? "border-[#00E676]/40 text-[#00E676]" : "border-neutral-700 text-neutral-500"}`}>
                    {a.retrieved_at ? "consulté par le projet" : "pas encore consulté"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>)}

      {tab === "dialogue" && (<>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Section title="Dialogue avec NEXUS" testid="brain-chat-panel">
            <div className="flex items-center gap-2 border-b border-border px-4 py-2">
              <button data-testid="new-session-button" onClick={() => openSession(null)}
                className="flex items-center gap-1 border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 transition-colors duration-150 hover:border-primary/50 hover:text-primary">
                <Plus size={11} /> Nouvelle
              </button>
              <select data-testid="session-select" value={sessionId || ""} onChange={(e) => openSession(e.target.value || null)}
                className="flex-1 border border-border bg-[#050505] px-2 py-1 font-mono text-[10px] text-neutral-300 outline-none">
                <option value="">— conversation en cours —</option>
                {sessions.map((s) => <option key={s.id} value={s.id}>{s.title} · {new Date(s.updated_at).toLocaleString("fr-CA", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</option>)}
              </select>
            </div>
            <div data-testid="brain-messages" className="h-[420px] space-y-4 overflow-y-auto px-5 py-4">
              {messages.length === 0 && !streaming && (
                <div className="pt-16 text-center font-mono text-lg text-neutral-500">
                  Pose une question à NEXUS — ex. « Comment va mon écosystème ? »
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={m.role === "user" ? "ml-12" : "mr-6"}>
                  <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${m.role === "user" ? "text-right text-neutral-500" : "text-primary"}`}>
                    {m.role === "user" ? "Mario" : "NEXUS"}
                  </div>
                  <div className={`mt-1 whitespace-pre-wrap border px-4 py-3 font-mono text-lg leading-relaxed ${m.role === "user" ? "border-border bg-muted/40 text-neutral-100" : m.error ? "border-red-500/40 bg-red-500/5 text-red-300" : "border-primary/25 bg-primary/5 text-neutral-100 shadow-[0_0_14px_rgba(0,229,255,0.05)]"}`}>
                    {m.content}
                  </div>
                  {m.role === "assistant" && <Sources sources={m.sources} />}
                </div>
              ))}
              {streaming && (
                <div className="mr-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">NEXUS</div>
                  <div className="mt-1 whitespace-pre-wrap border border-primary/25 bg-primary/5 px-4 py-3 font-mono text-lg leading-relaxed text-neutral-100">
                    {streamText || (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:300ms]" />
                        <span className="ml-1 text-neutral-400">réfléchit…</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="flex gap-2 border-t border-border p-3">
              <input data-testid="brain-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Parle à NEXUS…"
                className={inputCls} disabled={streaming} />
              <button data-testid="brain-chat-send" onClick={send} disabled={streaming || !input.trim()}
                className="flex items-center gap-2 bg-primary px-4 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black transition-opacity duration-150 hover:opacity-90 disabled:opacity-40">
                <Send size={12} />
              </button>
            </div>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title={`Mémoire — notes (${overview?.memory_notes ?? 0})`} testid="memory-panel">
            <div className="max-h-56 divide-y divide-border overflow-y-auto">
              {memory.length === 0 && <div className="px-4 py-4 font-mono text-[11px] text-neutral-600">Aucune note — lance une digestion</div>}
              {memory.map((n) => (
                <div key={n.id} className="px-4 py-3">
                  <div className="font-mono text-[10px] text-neutral-600">{fmtDate(n.created_at)} · {n.sources?.length ?? 0} sources</div>
                  <p className="mt-1 line-clamp-4 whitespace-pre-wrap font-mono text-lg leading-relaxed text-neutral-300">{n.note}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section title={`Connaissances durables (${knowledge.filter((k) => k.status === "active").length} actives)`} testid="knowledge-panel">
            <div className="max-h-64 divide-y divide-border overflow-y-auto">
              {knowledge.length === 0 && <div className="px-4 py-4 font-mono text-[11px] text-neutral-600">Aucune connaissance établie pour l'instant</div>}
              {knowledge.map((k) => (
                <div key={k.id} className="px-4 py-3">
                  <p className={`font-mono text-lg leading-relaxed ${k.status === "active" ? "text-neutral-200" : "text-neutral-600 line-through"}`}>{k.statement}</p>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-neutral-600">
                      {(k.environments || ["inconnu"]).map((e) => <EnvChip key={e} env={e} />)}
                      confiance {Math.round((k.confidence || 0) * 100)} % · {k.projects?.join(", ") || "global"} · établie {fmtDate(k.created_at)}
                    </span>
                    {k.status === "active" && (
                      <span className="flex gap-1">
                        <button data-testid={`confirm-knowledge-${k.id}`} onClick={() => knowledgeAction(k.id, "confirm")} title="Confirmer"
                          className="border border-border p-1 text-[#00E676] transition-colors duration-150 hover:border-[#00E676]/50"><Check size={11} /></button>
                        <button data-testid={`invalidate-knowledge-${k.id}`} onClick={() => knowledgeAction(k.id, "invalidate")} title="Invalider"
                          className="border border-border p-1 text-red-400 transition-colors duration-150 hover:border-red-500/50"><X size={11} /></button>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Directives permanentes" testid="directives-panel">
            <div className="divide-y divide-border">
              {directives.map((d) => (
                <div key={d.id} className="flex items-start justify-between gap-2 px-4 py-2.5">
                  <p className="font-mono text-lg text-neutral-300">{d.text}</p>
                  <button data-testid={`delete-directive-${d.id}`} onClick={async () => { await api.delete(`/brain/directives/${d.id}`); api.get("/brain/directives").then(({ data }) => setDirectives(data)); }}
                    className="text-neutral-600 transition-colors duration-150 hover:text-red-400"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 border-t border-border p-3">
              <input data-testid="new-directive-input" value={newDirective} onChange={(e) => setNewDirective(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDirective()} placeholder="Nouvelle consigne pour NEXUS…" className={inputCls} />
              <button data-testid="add-directive-button" onClick={addDirective}
                className="border border-border px-3 font-mono text-[10px] uppercase text-neutral-300 transition-colors duration-150 hover:border-primary/50 hover:text-primary"><Plus size={12} /></button>
            </div>
          </Section>
        </div>
      </div>
      </>)}

      <ReportModal report={report} onClose={() => setReport(null)} />
    </div>
  );
}
